// Colour and motion state for the blob, independent of which tier draws it.
//
// The product palette is warm and near-monochrome: bone at rest and while
// listening, a cool slate while thinking, amber while speaking. One hue is on
// screen at a time; states cross-fade between them. Deliberately not
// iridescent — no rainbow sweep, no chromatic aberration, no second hue.

export type BlobState = "idle" | "listening" | "thinking" | "speaking";

export type V3 = [number, number, number];

/** IQ cosine palette: colour(t) = a + b * cos(2pi * (c*t + d)). */
export interface Coeffs {
  a: V3;
  b: V3;
  c: V3;
  d: V3;
}

const STATES: readonly BlobState[] = ["idle", "listening", "thinking", "speaking"];

// Coefficients live in LINEAR light, because the shader gamma-encodes the frame
// at the end; a palette authored to look right as sRGB numbers renders pastel.
// b === a for every channel, so each channel bottoms out at zero and the ramp is
// a pure value ramp through one hue rather than a hue rotation.
//
// Solved, not eyeballed. a was fitted so that the MEAN colour of the rendered
// body lands on the design token — fitted through the whole pipeline, lighting
// and absorption and tone map included, because the palette is not what reaches
// the screen. Fitting palette(t) to the token directly leaves amber rendering as
// a traffic cone, since the absorption term eats its green and blue on the way.
// The per-channel offsets on d are under 0.04 (a 13 degree phase shift), which
// warms the core and cools the highlight by a hair without ever putting a second
// hue on screen.
//
// Measured in Chrome, mean over the rendered disc: bone #dfd8cc, cool #83a3b5,
// amber #eca551, against tokens #dfd8cc / #88a5b5 / #eca552. Re-measure before
// changing any lighting constant in shaders.ts — these are fitted TO it.
const BONE: Coeffs = {
  a: [0.9909, 0.8158, 0.6216],
  b: [0.9909, 0.8158, 0.6216],
  c: [1, 1, 1],
  d: [0.22, 0.232, 0.246],
};

const COOL: Coeffs = {
  a: [0.1965, 0.3099, 0.4062],
  b: [0.1965, 0.3099, 0.4062],
  c: [1, 1, 1],
  d: [0.246, 0.232, 0.22],
};

const AMBER: Coeffs = {
  a: [1.5703, 0.344, 0.0897],
  b: [1.5703, 0.344, 0.0897],
  c: [1, 1, 1],
  d: [0.22, 0.238, 0.256],
};

// Idle and listening share bone exactly, so that picking up the microphone is a
// change of behaviour and not a change of colour.
const PALETTES: Record<BlobState, Coeffs> = {
  idle: BONE,
  listening: BONE,
  thinking: COOL,
  speaking: AMBER,
};

// The rim, the specular and the contact edge all use a lightened version of the
// state's own hue rather than a fixed brand accent. A constant accent would put
// a second hue on the blob, which this design language does not allow.
const ACCENTS: Record<BlobState, V3> = {
  idle: [1.0, 0.955, 0.882],
  listening: [1.0, 0.955, 0.882],
  thinking: [0.696, 0.879, 1.0],
  speaking: [1.0, 0.642, 0.415],
};

/**
 * How long a transition INTO each state takes to settle, in seconds. From the
 * motion spec: thinking eases in slowly because it is a pause, speaking arrives
 * quickly, and listening is a barge-in — the user interrupting — so it is the
 * fastest thing the blob does.
 */
const SETTLE: Record<BlobState, number> = {
  idle: 0.752,
  listening: 0.12,
  thinking: 0.6,
  speaking: 0.24,
};

// --spring-blob: m=1, k=60, c=14 => zeta 0.904, no overshoot, 752ms settle.
// Faster transitions keep that damping ratio and scale k and c around it, so
// every state change has the same character and only its duration differs.
const SPRING_K = 60;
const SPRING_C = 14;
const SPRING_T = 0.752;

// Level smoothing per state, as time constants. Listening tracks the room
// evenly; speaking snaps open on a syllable and falls away slowly, which is what
// makes it read as a voice rather than a VU meter.
const LEVEL_TAU: Record<BlobState, { attack: number; release: number }> = {
  idle: { attack: 0.15, release: 0.15 },
  listening: { attack: 0.084, release: 0.084 },
  thinking: { attack: 0.15, release: 0.15 },
  speaking: { attack: 0.051, release: 0.2 },
};

/** Everything a tier needs to draw one frame. */
export interface BlobFrame {
  /** Seconds. Advances slowly under reduced motion and not at all once parked. */
  time: number;
  /** Enveloped loudness, 0..1. Always 0 under reduced motion. */
  level: number;
  coeffs: Coeffs;
  accent: V3;
  /** Base radius in world units, breath included. */
  r0: number;
  /** Radial displacement as a fraction of r0. Capped at 0.16 by the design. */
  ampFrac: number;
  freq: number;
  warpS: number;
  warp: number;
  dFreq: number;
  detail: number;
  flow: number;
  spin: number;
  sOct: number;
  /** Noise-origin walk; only thinking moves it. */
  origin: V3;
  /** Listening ring radius as a multiple of r0. */
  ringRadius: number;
  /** Linear-light amplitude of the ring. Zero outside listening. */
  ringAmp: number;
  reduced: boolean;
}

/** ACES filmic approximation, Narkowicz 2015. Matches the shader exactly. */
function aces(x: number): number {
  const y = (x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14);
  return y < 0 ? 0 : y > 1 ? 1 : y;
}

// The flat tiers have no lighting to run, so they need one point on the palette
// that stands in for the whole lit body. Fitted, not picked: this pair of
// numbers reproduces all three design tokens to within 4/255 per channel, which
// is why the 2D and CSS tiers land on the same colour the shader averages to.
const FLAT_T = 0.62;
const FLAT_EXPOSURE = 0.49;

function encode(linear: number): number {
  return Math.round(255 * Math.pow(aces(linear * FLAT_EXPOSURE), 1 / 2.2));
}

/** Evaluate the cosine palette in linear light. */
function evalPalette(p: Coeffs, t: number, out: V3): V3 {
  const TAU = 6.283185307179586;
  out[0] = p.a[0] + p.b[0] * Math.cos(TAU * (p.c[0] * t + p.d[0]));
  out[1] = p.a[1] + p.b[1] * Math.cos(TAU * (p.c[1] * t + p.d[1]));
  out[2] = p.a[2] + p.b[2] * Math.cos(TAU * (p.c[2] * t + p.d[2]));
  return out;
}

const SCRATCH: V3 = [0, 0, 0];

/** A linear-light colour, tone-mapped and encoded the way the shader does it. */
export function linearToCss(c: V3): string {
  return `rgb(${encode(c[0])}, ${encode(c[1])}, ${encode(c[2])})`;
}

/**
 * The state's colour as a flat CSS value, for the tiers that cannot shade.
 * `lighten` mixes toward white in display space — 0.06 is the design's "a 6%
 * lighter version of itself".
 */
export function paletteCss(p: Coeffs, lighten = 0): string {
  evalPalette(p, FLAT_T, SCRATCH);
  const r = encode(SCRATCH[0]);
  const g = encode(SCRATCH[1]);
  const b = encode(SCRATCH[2]);
  const lift = (v: number) => Math.round(v + (255 - v) * lighten);
  return `rgb(${lift(r)}, ${lift(g)}, ${lift(b)})`;
}

/**
 * Asymmetric breath: inhale over 40% of the cycle, exhale over 60%. A plain sine
 * reads as a machine; this reads as something alive. Returns 0..1.
 */
function breath(t: number, hz: number): number {
  const x = (t * hz) % 1;
  const p = x < 0.4 ? (x / 0.4) * 0.5 : 0.5 + ((x - 0.4) / 0.6) * 0.5;
  return -Math.cos(p * Math.PI * 2) * 0.5 + 0.5;
}

/**
 * The blob's whole animated state: a spring per state weight, a loudness
 * envelope, and a clock. Springs are integrated rather than tweened so that
 * velocity carries over — a barge-in landing halfway through a transition
 * continues from wherever the blob actually is instead of snapping to a new
 * curve.
 */
export class BlobMotion {
  readonly reduced: boolean;

  private weight: Record<BlobState, number> = { idle: 1, listening: 0, thinking: 0, speaking: 0 };
  private vel: Record<BlobState, number> = { idle: 0, listening: 0, thinking: 0, speaking: 0 };
  private target: BlobState = "idle";
  private k = SPRING_K;
  private c = SPRING_C;

  private rawLevel = 0;
  private level = 0;
  private clock = 0;

  private readonly frame: BlobFrame = {
    time: 0,
    level: 0,
    coeffs: { a: [0, 0, 0], b: [0, 0, 0], c: [0, 0, 0], d: [0, 0, 0] },
    accent: [0, 0, 0],
    r0: 0.66,
    ampFrac: 0,
    freq: 1.1,
    warpS: 0.22,
    warp: 0.45,
    dFreq: 3.4,
    detail: 0.0025,
    flow: 0.1,
    spin: 0.035,
    sOct: 2,
    origin: [0, 0, 0],
    ringRadius: 1.06,
    ringAmp: 0,
    reduced: false,
  };

  constructor() {
    // matchMedia is absent in some embedded webviews; a missing query must not
    // stop the blob from rendering at all.
    let reduced = false;
    try {
      reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      reduced = false;
    }
    this.reduced = reduced;
    this.frame.reduced = reduced;
  }

  setState(s: BlobState): void {
    if (s === this.target) return;
    this.target = s;
    // Reduced motion swaps colour over a flat 200ms and never deforms, so every
    // transition uses the same short spring.
    const t = this.reduced ? 0.2 : SETTLE[s];
    const scale = SPRING_T / t;
    this.k = SPRING_K * scale * scale;
    this.c = SPRING_C * scale;
  }

  setLevel(v: number): void {
    this.rawLevel = v < 0 ? 0 : v > 1 ? 1 : v;
  }

  /** True once the weights and the level have stopped moving. */
  get settled(): boolean {
    for (const s of STATES) {
      const want = s === this.target ? 1 : 0;
      if (Math.abs(this.weight[s] - want) > 0.002 || Math.abs(this.vel[s]) > 0.01) return false;
    }
    return Math.abs(this.level - this.rawLevel) < 0.002;
  }

  /** Advance by `dt` seconds and return the frame to draw. Do not retain it. */
  step(dt: number): BlobFrame {
    this.integrate(dt);

    // A backgrounded tab or a long GC would otherwise teleport the noise field.
    const clamped = dt > 1 / 20 ? 1 / 20 : dt;
    this.clock += clamped * (this.reduced ? 0 : 1);

    const w = this.weight;
    const f = this.frame;
    const M = (i: number, l: number, t: number, s: number) =>
      i * w.idle + l * w.listening + t * w.thinking + s * w.speaking;

    // Soft saturation: a shout must not turn the blob into a starfish.
    const lv = this.reduced ? 0 : this.level / (1 + 0.7 * this.level);
    const r = this.reduced ? 0 : 1;

    // Blend the COEFFICIENTS, never the final RGB. Every convex combination of
    // (a,b,c,d) is itself a well-formed palette, so the cross-fade walks a
    // continuous family of good palettes; mixing amber and slate as RGB would
    // pass through grey and read as a rendering fault.
    const idle = PALETTES.idle;
    const listening = PALETTES.listening;
    const thinking = PALETTES.thinking;
    const speaking = PALETTES.speaking;
    const c = f.coeffs;
    for (let i = 0; i < 3; i++) {
      const key = i as 0 | 1 | 2;
      c.a[key] = M(idle.a[key], listening.a[key], thinking.a[key], speaking.a[key]);
      c.b[key] = M(idle.b[key], listening.b[key], thinking.b[key], speaking.b[key]);
      c.c[key] = M(idle.c[key], listening.c[key], thinking.c[key], speaking.c[key]);
      c.d[key] = M(idle.d[key], listening.d[key], thinking.d[key], speaking.d[key]);
      f.accent[key] = M(
        ACCENTS.idle[key],
        ACCENTS.listening[key],
        ACCENTS.thinking[key],
        ACCENTS.speaking[key],
      );
    }

    const breathHz = M(1 / 5.2, 1 / 4.4, 1 / 3.6, 1 / 3.2);
    const breathAmt = M(0.018, 0.014, 0.01, 0.008) * r;
    f.r0 = 0.66 * (1 + breathAmt * breath(this.clock, breathHz));

    // Amplitudes are fractions of the radius, straight from the state table.
    // The design caps deformation at 0.16r; past that the blob stops reading as
    // one body and starts reading as a lava lamp.
    const amp = M(0.012, 0.012 + 0.09 * lv, 0.008, 0.016 + 0.14 * lv) * r;
    f.ampFrac = Math.min(amp, 0.16);

    f.freq = M(1.1, 1.2, 1.3, 1.15);
    f.warpS = M(0.22, 0.24, 0.32, 0.26) * r;
    f.warp = M(0.45, 0.5, 0.75, 0.55) * r;
    // Zeroing the detail frequency under reduced motion collapses the noise
    // field to a constant, which is what turns the body into the static gradient
    // disc the accessibility spec asks for rather than a field of frozen speckle.
    f.dFreq = M(3.4, 3.6, 4.4, 3.6) * r;
    f.detail = M(0.0025, 0.003, 0.0045, 0.0032 + 0.0014 * lv) * r;
    f.flow = M(0.1, 0.14, 0.055, 0.22) * r;
    f.spin = M(0.035, 0.055, 0.13, 0.09) * r;
    f.sOct = 2;

    // Thinking walks the noise origin around a small circle: the interior drifts
    // without the silhouette moving, which is what "considering it" looks like.
    const walk = w.thinking * 0.35 * r;
    f.origin[0] = Math.cos(this.clock * 0.55) * walk;
    f.origin[1] = Math.sin(this.clock * 0.55) * walk;
    f.origin[2] = 0;

    // The ring is specified as 8% opacity. Opacity is a display-space quantity
    // and the shader works in linear light, so convert: 0.08^2.2.
    const RING_LINEAR = Math.pow(0.08, 2.2);
    f.ringRadius = 1.06 + 0.02 * lv;
    f.ringAmp = w.listening * RING_LINEAR * (0.6 + 0.4 * lv) * r;

    f.time = this.clock;
    f.level = lv;
    return f;
  }

  private integrate(dt: number): void {
    // Semi-implicit Euler at a fixed substep. The barge-in spring is stiff
    // (k ~ 2400) and a 30fps frame would integrate it into an oscillation.
    const STEP = 1 / 300;
    let left = dt > 0.25 ? 0.25 : dt;
    const k = this.k;
    const c = this.c;

    while (left > 0) {
      const h = left > STEP ? STEP : left;
      left -= h;
      for (const s of STATES) {
        const want = s === this.target ? 1 : 0;
        const v = this.vel[s] + h * (k * (want - this.weight[s]) - c * this.vel[s]);
        this.vel[s] = v;
        this.weight[s] = this.weight[s] + h * v;
      }
    }

    // Clamp then renormalise: the blended coefficients are only guaranteed to be
    // a valid palette when the weights are a convex combination.
    let sum = 0;
    for (const s of STATES) {
      if (this.weight[s] < 0) this.weight[s] = 0;
      sum += this.weight[s];
    }
    if (sum > 1e-4) for (const s of STATES) this.weight[s] /= sum;
    else this.weight[this.target] = 1;

    const tau = LEVEL_TAU[this.target];
    const t = this.rawLevel > this.level ? tau.attack : tau.release;
    this.level += (this.rawLevel - this.level) * (1 - Math.exp(-dt / Math.max(t, 1e-4)));
  }
}
